import React, { useEffect, useState } from "react";
import { Avatar, Box, SxProps, Theme, Typography } from "@mui/material";

interface ImageStackProps {
  images: (string | File)[]; // supports both File and string
  onClick?: () => void;
  size?: number; // allow resizing (default: 40px)
  sx?: SxProps<Theme>;
}

const fallbackImage = "/default.jpg";

const ImageStack: React.FC<ImageStackProps> = ({
  images = [],
  onClick,
  size = 40,
  sx = {},
}) => {
  const [mainSrc, setMainSrc] = useState<string>(fallbackImage);

  useEffect(() => {
    if (!images || images.length === 0) {
      setMainSrc(fallbackImage);
      return;
    }

    const first = images[0];
    const src = typeof first === "string" ? first : URL.createObjectURL(first);
    setMainSrc(src);

    // Cleanup object URL if it's a File
    return () => {
      if (first instanceof File) URL.revokeObjectURL(src);
    };
  }, [images]);

  const handleError = () => setMainSrc(fallbackImage);

  const extraCount = images.length > 1 ? images.length - 1 : 0;

  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        width: size,
        height: size,
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s ease",
        "&:hover": {
          transform: onClick ? "scale(1.05)" : "none",
        },
        ...sx,
      }}
    >
      {/* Base fallback background */}
      <Avatar
        variant="square"
        src={fallbackImage}
        alt="Fallback"
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          bgcolor: "#bee3f4ff",
        }}
      />

      {/* Main image */}
      <Avatar
        variant="square"
        src={mainSrc}
        alt="Main"
        onError={handleError}
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          bgcolor: "transparent",
        }}
      />

      {/* Overlay for extra count */}
      {extraCount > 0 && (
        <Avatar
          variant="square"
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0,0,0,0.01)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              textShadow: "0px 0px 6px black",
              fontSize: "14px",
              fontWeight: 500,
            }}
          >
            +{extraCount}
          </Typography>
        </Avatar>
      )}
    </Box>
  );
};

export default ImageStack;
